import { Scale, Shield, FileSearch, AlertTriangle, CheckCircle, Clock, Gavel, Target, BookOpen, ArrowRight, TrendingUp, AlertCircle } from "lucide-react";

const scoreColor = (v) =>
  v == null ? "text-muted-foreground" : v >= 70 ? "text-emerald-400" : v >= 40 ? "text-amber-400" : "text-red-400";

const scoreBg = (v) =>
  v == null ? "bg-muted/20" : v >= 70 ? "bg-emerald-500/10 border-emerald-500/20" : v >= 40 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

const relevanceBadge = {
  High: "bg-red-500/10 text-red-400 border-red-500/20",
  Medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  Low: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

const solStatusColor = {
  Active: "text-emerald-400",
  "At Risk": "text-amber-400",
  "Potentially Expired": "text-red-400",
  Unknown: "text-muted-foreground",
};

export default function LegalAnalysisDisplay({ analysis, onReanalyze, reanalyzing }) {
  if (!analysis || analysis.legal_score == null) {
    return (
      <div className="text-center py-12 border border-border/30 rounded-xl" data-testid="no-legal-analysis">
        <Scale className="w-8 h-8 mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-sm text-muted-foreground">No legal analysis available yet</p>
        {onReanalyze && (
          <button onClick={onReanalyze} disabled={reanalyzing}
            className="mt-3 px-4 py-2 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-all disabled:opacity-50"
            data-testid="trigger-analysis-btn">
            {reanalyzing ? "Analyzing..." : "Run Legal Analysis"}
          </button>
        )}
      </div>
    );
  }

  const la = analysis;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="legal-analysis-section">
      {/* Top Scores Row */}
      <div className="grid grid-cols-3 gap-3" data-testid="legal-scores-grid">
        {[
          { label: "Legal Score", value: la.legal_score, icon: Scale },
          { label: "Case Validity", value: la.case_validity_score, icon: Shield },
          { label: "Evidence", value: la.evidence_strength_score, icon: FileSearch },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className={`p-3 sm:p-4 rounded-xl border ${scoreBg(value)} text-center`}>
            <Icon className={`w-4 h-4 mx-auto mb-1.5 ${scoreColor(value)}`} />
            <div className={`text-2xl font-bold ${scoreColor(value)}`}>{value ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Key Indicators */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="legal-indicators">
        {[
          { label: "Damages", value: la.damages_potential, icon: TrendingUp },
          { label: "Complexity", value: la.case_complexity, icon: Target },
          { label: "SOL Status", value: la.sol_status, icon: Clock, color: solStatusColor[la.sol_status] },
          { label: "Case Type", value: la.recommended_case_type?.split(" - ")[0] || "—", icon: Gavel },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="p-3 rounded-xl border border-border/30 bg-card/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon className="w-3 h-3 text-primary/50" />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
            <p className={`text-sm font-semibold truncate ${color || "text-foreground"}`}>{value || "—"}</p>
          </div>
        ))}
      </div>

      {/* Attorney Pitch */}
      {la.attorney_pitch && la.attorney_pitch !== "Analysis unavailable — please review manually." && (
        <div className="p-4 rounded-xl border border-primary/20 bg-primary/5" data-testid="attorney-pitch">
          <div className="flex items-center gap-2 mb-2">
            <Gavel className="w-4 h-4 text-primary" />
            <span className="text-xs text-primary uppercase tracking-wider font-semibold">Attorney Pitch</span>
          </div>
          <p className="text-sm text-foreground leading-relaxed">{la.attorney_pitch}</p>
        </div>
      )}

      {/* Applicable Laws */}
      {la.applicable_laws?.length > 0 && (
        <div className="p-4 rounded-xl border border-border/30 bg-card/20" data-testid="applicable-laws">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-primary/60" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Applicable Laws & Codes</span>
          </div>
          <div className="space-y-2">
            {la.applicable_laws.map((law, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-secondary/20">
                <span className={`mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium border ${relevanceBadge[law.relevance] || relevanceBadge.Low}`}>
                  {law.relevance}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{law.code}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{law.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strengths & Red Flags side-by-side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {la.case_strengths?.length > 0 && (
          <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5" data-testid="case-strengths">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-400 uppercase tracking-wider font-medium">Strengths</span>
            </div>
            <ul className="space-y-1.5">
              {la.case_strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="w-1 h-1 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {la.red_flags?.length > 0 && (
          <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5" data-testid="red-flags">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-red-400 uppercase tracking-wider font-medium">Red Flags</span>
            </div>
            <ul className="space-y-1.5">
              {la.red_flags.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="w-1 h-1 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Required Evidence */}
      {la.required_evidence?.length > 0 && (
        <div className="p-4 rounded-xl border border-border/30 bg-card/20" data-testid="required-evidence">
          <div className="flex items-center gap-2 mb-2">
            <FileSearch className="w-4 h-4 text-primary/60" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Required Evidence</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {la.required_evidence.map((item, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-secondary/40 text-foreground border border-border/30">{item}</span>
            ))}
          </div>
        </div>
      )}

      {/* SOL & Jurisdiction Notes */}
      {(la.sol_notes || la.jurisdiction_notes) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {la.sol_notes && (
            <div className="p-3 rounded-xl border border-border/30 bg-card/20">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="w-3 h-3 text-primary/50" />
                <span className="text-xs text-muted-foreground">Statute of Limitations</span>
              </div>
              <p className="text-xs text-foreground leading-relaxed">{la.sol_notes}</p>
            </div>
          )}
          {la.jurisdiction_notes && (
            <div className="p-3 rounded-xl border border-border/30 bg-card/20">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertCircle className="w-3 h-3 text-primary/50" />
                <span className="text-xs text-muted-foreground">Jurisdiction Notes</span>
              </div>
              <p className="text-xs text-foreground leading-relaxed">{la.jurisdiction_notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Comparable Outcomes */}
      {la.comparable_outcomes && (
        <div className="p-3 rounded-xl border border-border/30 bg-card/20" data-testid="comparable-outcomes">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-3 h-3 text-primary/50" />
            <span className="text-xs text-muted-foreground">Comparable Outcomes</span>
          </div>
          <p className="text-xs text-foreground leading-relaxed">{la.comparable_outcomes}</p>
        </div>
      )}

      {/* Next Steps */}
      {la.next_steps?.length > 0 && (
        <div className="p-4 rounded-xl border border-border/30 bg-card/20" data-testid="next-steps">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRight className="w-4 h-4 text-primary/60" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Recommended Next Steps</span>
          </div>
          <ol className="space-y-1.5">
            {la.next_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Timestamp & Re-analyze */}
      <div className="flex items-center justify-between pt-2">
        <span className="text-xs text-muted-foreground/50">
          {la.analyzed_at ? `Analyzed ${new Date(la.analyzed_at).toLocaleString()}` : ""}
        </span>
        {onReanalyze && (
          <button onClick={onReanalyze} disabled={reanalyzing}
            className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 disabled:opacity-50"
            data-testid="reanalyze-btn">
            {reanalyzing ? <div className="w-3 h-3 border-2 border-primary/40 border-t-primary rounded-full animate-spin" /> : <Scale className="w-3 h-3" />}
            {reanalyzing ? "Analyzing..." : "Re-analyze"}
          </button>
        )}
      </div>
    </div>
  );
}
